import { Link, Navigate, useParams } from "react-router-dom";
import {
  Phone,
  ArrowRight,
  Check,
  MapPin,
  ShieldCheck,
  FileText,
  Clock,
  Users,
  Star,
  Quote,
  ChevronRight,
  Home as HomeIcon,
  BadgeCheck,
} from "lucide-react";
import {
  COMPANY,
  CITIES,
  PRO_AUDIENCES,
  PRO_PARTNERS,
} from "../lib/constants";
import useSeo, { breadcrumbJsonLd, SITE } from "../lib/useSeo";

const VALUE_PROPS = [
  { icon: Users, t: "Référent unique", d: "Un seul interlocuteur pour tout votre parc — réponse sous 1h en jours ouvrés." },
  { icon: FileText, t: "Devis sous 24h", d: "Estimations chiffrées rapides, formats compatibles avec vos outils de gestion." },
  { icon: Clock, t: "Astreinte 24/7", d: "Une ligne dédiée pour les urgences, week-ends et jours fériés inclus." },
  { icon: ShieldCheck, t: "Facturation pro", d: "TVA, BAT, multi-sites, paiement à 30 jours, attestations sur demande." },
];

export default function AudiencePage({ audienceSlug }) {
  const params = useParams();
  const slug = audienceSlug || params.slug;
  const audience = PRO_AUDIENCES.find((a) => a.slug === slug);

  const partner = audience ? PRO_PARTNERS[audience.partner] : null;
  const cities = audience
    ? audience.cities
        .map((s) => CITIES.find((c) => c.slug === s))
        .filter(Boolean)
    : [];

  const path = audience ? `/serrurier-${audience.slug}` : "/professionnels";
  const title = audience?.metaTitle;
  const description = audience?.metaDescription;

  const jsonLdList = audience
    ? [
        breadcrumbJsonLd([
          { name: "Accueil", path: "/" },
          { name: "Professionnels", path: "/professionnels" },
          { name: audience.h1, path },
        ]),
        {
          "@context": "https://schema.org",
          "@type": "Service",
          "@id": `${SITE.url}${path}#service`,
          name: audience.h1,
          serviceType: `Locksmith services for ${audience.keyword}`,
          provider: {
            "@type": "Locksmith",
            "@id": `${SITE.url}/#business`,
            name: SITE.name,
            telephone: "+33780939300",
            email: "contact@monserrurierbreton.fr",
            url: SITE.url,
          },
          areaServed: cities.map((c) => ({
            "@type": "City",
            name: c.name,
            address: {
              "@type": "PostalAddress",
              postalCode: c.postal,
              addressCountry: "FR",
            },
          })),
          audience: {
            "@type": "BusinessAudience",
            audienceType: audience.keyword,
          },
          hasOfferCatalog: {
            "@type": "OfferCatalog",
            name: `Services serrurerie — ${audience.keyword}`,
            itemListElement: audience.services.map((s) => ({
              "@type": "Offer",
              itemOffered: { "@type": "Service", name: s },
            })),
          },
        },
        {
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: audience.faq.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
          })),
        },
      ]
    : [];

  useSeo({ title, description, path, jsonLd: jsonLdList });

  if (!audience) return <Navigate to="/professionnels" replace />;

  return (
    <div data-testid={`page-audience-${audience.slug}`}>
      {/* Breadcrumb */}
      <nav
        className="max-w-7xl mx-auto px-4 md:px-8 pt-8 text-xs text-slate-500 flex items-center gap-2 flex-wrap"
        aria-label="Fil d'Ariane"
      >
        <Link to="/" className="inline-flex items-center gap-1 hover:text-teal-700">
          <HomeIcon className="w-3.5 h-3.5" /> Accueil
        </Link>
        <ChevronRight className="w-3.5 h-3.5" />
        <Link to="/professionnels" className="hover:text-teal-700">
          Professionnels
        </Link>
        <ChevronRight className="w-3.5 h-3.5" />
        <span className="text-slate-900 font-semibold">{audience.eyebrow}</span>
      </nav>

      {/* Hero */}
      <section className="relative overflow-hidden bg-slate-900 text-white mt-6">
        <div className="absolute inset-0 opacity-[0.07] [background-image:radial-gradient(#5eead4_1px,transparent_1px)] [background-size:18px_18px]" />
        <div className={`absolute -top-40 -right-40 w-[500px] h-[500px] rounded-full bg-gradient-to-br ${audience.accent} opacity-20 blur-3xl`} />
        <div className="relative max-w-7xl mx-auto px-4 md:px-8 pt-12 md:pt-20 pb-16 md:pb-20">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
            <div className="lg:col-span-7 space-y-6">
              <div className={`inline-flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-teal-300`}>
                <span className="h-px w-8 bg-teal-300" /> {audience.eyebrow}
              </div>
              <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tighter leading-[0.95]">
                {audience.h1.split(" ").slice(0, 2).join(" ")}{" "}
                <span className="text-teal-300">
                  {audience.h1.split(" ").slice(2).join(" ")}
                </span>
                .
              </h1>
              <p className="text-slate-300 text-lg max-w-2xl leading-relaxed">
                {audience.hero.lead}
              </p>
              <div className="flex flex-wrap gap-3 pt-2">
                <a
                  data-testid="audience-hero-call"
                  href={`tel:${COMPANY.phoneDigits}`}
                  className="inline-flex items-center gap-3 bg-orange-500 hover:bg-orange-600 text-white font-bold rounded-full px-7 py-4 shadow-2xl shadow-orange-600/30 transition-colors"
                >
                  <Phone className="w-5 h-5" />
                  {COMPANY.phone}
                </a>
                <Link
                  data-testid="audience-hero-form"
                  to="/professionnels#pro-form"
                  className="inline-flex items-center gap-2 bg-white text-slate-900 font-bold rounded-full px-6 py-4 hover:bg-teal-50 transition-colors"
                >
                  Devis cadre <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
              <div className="flex flex-wrap gap-2 pt-4 border-t border-slate-800">
                <span className="text-xs text-slate-400 font-semibold uppercase tracking-widest pt-3 mr-2">
                  Zones prioritaires :
                </span>
                {audience.hero.heroCities.map((c) => (
                  <span
                    key={c}
                    className="inline-flex items-center gap-1.5 bg-slate-800/60 border border-slate-700 text-teal-200 rounded-full px-3 py-1.5 text-xs font-semibold mt-2"
                  >
                    <MapPin className="w-3 h-3" /> {c}
                  </span>
                ))}
              </div>
            </div>

            <div className="lg:col-span-5">
              <div className="grid grid-cols-2 gap-3">
                {VALUE_PROPS.map((v) => (
                  <div
                    key={v.t}
                    className="bg-slate-800/80 backdrop-blur border border-slate-700 rounded-2xl p-5"
                  >
                    <v.icon className="w-6 h-6 text-teal-300 mb-3" />
                    <div className="font-display font-bold text-white text-lg leading-tight">
                      {v.t}
                    </div>
                    <div className="text-sm text-slate-400 mt-1.5 leading-relaxed">
                      {v.d}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Cas d'usage */}
      <section className="max-w-7xl mx-auto px-4 md:px-8 py-20 md:py-28">
        <div className="max-w-2xl mb-12">
          <div className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-teal-700 mb-3">
            <span className="h-px w-8 bg-teal-700" /> Cas d'usage
          </div>
          <h2 className="font-display text-3xl md:text-5xl font-extrabold tracking-tight text-slate-900 leading-tight">
            Les situations qu'on gère
            <br />
            <span className="text-teal-600">au quotidien.</span>
          </h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {audience.useCases.map((u, i) => (
            <article
              key={u.t}
              data-testid={`audience-usecase-${i + 1}`}
              className="bg-white border border-slate-200 rounded-3xl p-7 hover:border-teal-300 hover:shadow-xl hover:shadow-teal-900/5 transition-all group relative overflow-hidden"
            >
              <div className="absolute -top-6 -right-4 font-display font-black text-slate-100 text-8xl leading-none select-none">
                {i + 1}
              </div>
              <div className="relative">
                <div className={`w-12 h-12 rounded-2xl bg-gradient-to-br ${audience.accent} text-white flex items-center justify-center mb-4 font-display font-extrabold`}>
                  0{i + 1}
                </div>
                <h3 className="font-display text-lg md:text-xl font-bold text-slate-900 mb-2">
                  {u.t}
                </h3>
                <p className="text-slate-600 leading-relaxed">{u.d}</p>
              </div>
            </article>
          ))}
        </div>
      </section>

      {/* Services dédiés */}
      <section className="bg-slate-50 border-y border-slate-200">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-20 md:py-28">
          <div className="max-w-2xl mb-12">
            <div className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-teal-700 mb-3">
              <span className="h-px w-8 bg-teal-700" /> Services dédiés
            </div>
            <h2 className="font-display text-3xl md:text-5xl font-extrabold tracking-tight text-slate-900 leading-tight">
              Tout ce qu'il vous faut,
              <br />
              <span className="text-teal-600">sans intermédiaire.</span>
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {audience.services.map((s, i) => (
              <div
                key={s}
                data-testid={`audience-service-${i + 1}`}
                className="bg-white border border-slate-200 rounded-2xl p-5 hover:border-teal-400 transition-colors"
              >
                <div className="w-8 h-8 rounded-lg bg-teal-50 text-teal-700 flex items-center justify-center mb-3 group-hover:bg-teal-600 group-hover:text-white transition-colors">
                  <Check className="w-4 h-4" />
                </div>
                <div className="font-display font-bold text-slate-900 text-sm leading-snug">
                  {s}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Partenaire témoignage */}
      {partner && (
        <section className="max-w-7xl mx-auto px-4 md:px-8 py-20 md:py-24">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-5">
              <div className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-teal-700 mb-3">
                <BadgeCheck className="w-4 h-4" /> Partenaire actuel
              </div>
              <h2 className="font-display text-3xl md:text-4xl font-extrabold tracking-tight text-slate-900 leading-tight">
                Ils nous font
                <br />
                <span className="text-teal-600">déjà confiance.</span>
              </h2>
              <p className="text-slate-600 mt-4 leading-relaxed">
                Un témoignage parmi d'autres — d'un professionnel local qui
                travaille avec nous au quotidien.
              </p>
              <Link
                to="/professionnels"
                className="mt-6 inline-flex items-center gap-2 text-teal-700 font-bold hover:underline"
              >
                Voir tous nos partenaires <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
            <div className="lg:col-span-7">
              <article
                data-testid="audience-partner-card"
                className="relative bg-white border border-slate-200 rounded-3xl p-8 md:p-10 shadow-xl"
              >
                <Quote className="absolute top-6 right-6 w-12 h-12 text-slate-200" />
                <div className="flex items-center gap-1 mb-5">
                  {[...Array(partner.rating)].map((_, idx) => (
                    <Star key={idx} className="w-5 h-5 fill-amber-400 text-amber-400" />
                  ))}
                </div>
                <p className="text-slate-700 leading-relaxed text-lg mb-6 italic">
                  « {partner.text} »
                </p>
                <div className="pt-5 border-t border-slate-100 flex items-center gap-4">
                  <div className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${partner.accent} text-white font-display font-extrabold flex items-center justify-center text-base flex-shrink-0`}>
                    {partner.initials}
                  </div>
                  <div>
                    <div className="font-display font-extrabold text-slate-900 text-lg">
                      {partner.company}
                    </div>
                    <div className="text-sm text-slate-500">
                      {partner.contact} · {partner.role}
                    </div>
                    <div className="text-[10px] uppercase tracking-widest text-teal-700 font-bold mt-1">
                      {partner.city}
                    </div>
                  </div>
                </div>
              </article>
            </div>
          </div>
        </section>
      )}

      {/* Villes desservies — SEO long-tail */}
      <section className="bg-slate-50 border-y border-slate-200">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-20 md:py-24">
          <div className="max-w-2xl mb-10">
            <div className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-teal-700 mb-3">
              <MapPin className="w-4 h-4" /> Zones desservies
            </div>
            <h2 className="font-display text-3xl md:text-5xl font-extrabold tracking-tight text-slate-900 leading-tight">
              Serrurier {audience.keyword}
              <br />
              <span className="text-teal-600">dans toute la Bretagne 22 & 35.</span>
            </h2>
            <p className="text-slate-600 mt-4 leading-relaxed">
              Nous intervenons sous 30 minutes en moyenne sur l'ensemble de la
              Côte d'Émeraude et son arrière-pays — un rayon de 30 km autour
              de Mont-Dol (Bretagne, départements{" "}
              <strong>22</strong> et <strong>35</strong>).
            </p>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
            {cities.map((c) => (
              <Link
                key={c.slug}
                to={`/serrurier-${c.slug}`}
                data-testid={`audience-city-link-${c.slug}`}
                className="group bg-white border border-slate-200 rounded-2xl px-4 py-3 hover:border-teal-500 hover:shadow-md transition-all"
              >
                <div className="font-display font-bold text-slate-900 text-sm group-hover:text-teal-700 leading-tight">
                  Serrurier {c.name}
                </div>
                <div className="text-[10px] text-slate-500 uppercase tracking-widest mt-0.5">
                  {c.postal}
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ — SEO + Schema */}
      <section className="max-w-5xl mx-auto px-4 md:px-8 py-20 md:py-24">
        <div className="max-w-2xl mb-10">
          <div className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-teal-700 mb-3">
            <span className="h-px w-8 bg-teal-700" /> Questions fréquentes
          </div>
          <h2 className="font-display text-3xl md:text-5xl font-extrabold tracking-tight text-slate-900 leading-tight">
            Tout ce qu'on vous a déjà demandé.
          </h2>
        </div>
        <div className="space-y-3">
          {audience.faq.map((f, i) => (
            <details
              key={i}
              data-testid={`audience-faq-${i + 1}`}
              className="group bg-white border border-slate-200 rounded-2xl px-6 py-5 open:shadow-lg open:border-teal-200 transition-all"
              open={i === 0}
            >
              <summary className="flex items-center justify-between gap-4 cursor-pointer list-none">
                <span className="font-display font-bold text-slate-900 text-base md:text-lg">
                  {f.q}
                </span>
                <span className="w-8 h-8 rounded-full bg-slate-100 group-open:bg-teal-600 group-open:text-white text-slate-500 flex items-center justify-center transition-colors flex-shrink-0">
                  <ArrowRight className="w-4 h-4 group-open:rotate-90 transition-transform" />
                </span>
              </summary>
              <p className="mt-4 text-slate-600 leading-relaxed">{f.a}</p>
            </details>
          ))}
        </div>
      </section>

      {/* SEO copy block */}
      <section className="max-w-7xl mx-auto px-4 md:px-8 pb-20">
        <div className="bg-white border border-slate-200 rounded-3xl p-8 md:p-12">
          <h2 className="font-display text-2xl md:text-3xl font-extrabold text-slate-900 mb-4">
            {audience.h1} — Côte d'Émeraude & Bretagne
          </h2>
          <div className="prose prose-slate max-w-none text-slate-700 leading-relaxed text-sm md:text-base space-y-4">
            <p>
              Mon Serrurier Breton est l'<strong>artisan serrurier de référence</strong>{" "}
              pour les <strong>{audience.keyword}</strong> de Bretagne, et plus
              particulièrement sur la Côte d'Émeraude. Nos équipes interviennent
              chaque jour à <strong>Saint-Malo</strong>, <strong>Dinard</strong>,{" "}
              <strong>Dinan</strong>, <strong>Cancale</strong> et l'ensemble des
              communes du <strong>22 et 35</strong>, avec un délai d'arrivée
              moyen de 30 minutes même en astreinte de nuit.
            </p>
            <p>
              Nous comprenons que pour un professionnel — qu'il soit{" "}
              {audience.keyword === "hôteliers"
                ? "directeur d'hôtel ou gouvernante"
                : audience.keyword === "syndics & copropriétés"
                ? "gestionnaire de syndic ou conseil syndical"
                : audience.keyword === "agences immobilières"
                ? "négociateur immobilier ou bailleur"
                : "concierge ou propriétaire de location courte durée"}{" "}
              — chaque minute de blocage coûte. Notre engagement : un référent
              unique pour tout votre parc, des tarifs négociés sur contrat
              cadre, une facturation pro à 30 jours, et un reporting clair
              de chaque intervention.
            </p>
            <p>
              <strong>Mots-clés ciblés sur cette zone :</strong>{" "}
              {audience.keywords.join(" · ")}.
            </p>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="max-w-7xl mx-auto px-4 md:px-8 pb-20">
        <div className="rounded-3xl bg-gradient-to-br from-slate-900 via-slate-900 to-teal-900 p-10 md:p-14 text-white relative overflow-hidden">
          <div className="absolute -bottom-20 -left-20 w-80 h-80 rounded-full bg-amber-400/20 blur-3xl" />
          <div className="relative grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
            <div>
              <h2 className="font-display text-3xl md:text-4xl font-extrabold tracking-tight leading-tight">
                Prêt à parler de
                <br />
                <span className="text-amber-300">votre parc ?</span>
              </h2>
              <p className="text-teal-100 mt-3 max-w-md">
                Audit gratuit, devis cadre sous 24h, mise en service rapide.
              </p>
            </div>
            <div className="flex md:justify-end flex-wrap gap-3">
              <a
                data-testid="audience-final-call"
                href={`tel:${COMPANY.phoneDigits}`}
                className="inline-flex items-center gap-2 bg-orange-500 hover:bg-orange-600 text-white font-bold rounded-full px-7 py-4 shadow-xl text-lg"
              >
                <Phone className="w-5 h-5" />
                {COMPANY.phone}
              </a>
              <Link
                to="/professionnels#pro-form"
                className="inline-flex items-center gap-2 bg-white text-slate-900 font-bold rounded-full px-6 py-4"
              >
                Demander un devis <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
